import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from 'auth';
import { Box, CircularProgress, Typography } from '@mui/material';
import theme from 'theme';
import { getCurrentVacationPeriod, getNextVacationPeriod } from './getVacationPeriods';
import { DarkTooltip } from '../../../components/DarkTooltip';

const VacationTileCurrent = ({ user }) => {
  const { tenant } = useAuth();
  const TOTAL_VACATION_DAYS = tenant?.vacation.totalDays;

  const [currentVacationPeriod, setCurrentVacationPeriod] = useState('');
  const [nextVacationPeriod, setNextVacationPeriod] = useState('');

  useEffect(() => {
    setCurrentVacationPeriod(getCurrentVacationPeriod());
  }, [setCurrentVacationPeriod]);

  useEffect(() => {
    setNextVacationPeriod(getNextVacationPeriod());
  }, [setNextVacationPeriod]);

  const currentRemainingDays =
    user.availableDays < 0
      ? TOTAL_VACATION_DAYS + user.availableDays || TOTAL_VACATION_DAYS
      : user.availableDays;

  const accessibleRemainingText =
    currentRemainingDays === 1
      ? '1 vacation day remaining'
      : `${currentRemainingDays} vacation days remaining`;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '0',
          gap: '1.5rem',
          flex: 'none',
          order: '0',
          flexGrow: '0',
        }}
      >
        <Box sx={{ alignItems: 'flex-start', display: 'flex', flexDirection: 'column' }}>
          <Typography
            sx={{
              marginTop: theme.spacing(1),
              marginBottom: theme.spacing(1),
              fontWeight: 500,
              fontStyle: 'normal',
              color: '#000000',
            }}
            variant="body1"
          >
            <Box component="span">
              {user.availableDays < 0
                ? -user.availableDays
                : TOTAL_VACATION_DAYS - user.availableDays}
              {TOTAL_VACATION_DAYS - user.availableDays === 1 || user.availableDays === -1
                ? ' day '
                : ' days '}
              used
            </Box>
          </Typography>
          {user.availableDays > 0 || user.availableDays === 0 ? (
            <Typography
              sx={{ fontStyle: 'normal', fontWeight: 'normal', color: '#4c4c4c' }}
              variant="body2"
              component="div"
            >
              {currentVacationPeriod}
            </Typography>
          ) : (
            user.availableDays < 0 && (
              <Typography
                sx={{ fontStyle: 'normal', fontWeight: 'normal', color: '#4c4c4c' }}
                variant="body2"
                component="div"
              >
                {nextVacationPeriod}
              </Typography>
            )
          )}
        </Box>
      </Box>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          size={75}
          thickness={0.5}
          sx={{
            color: (() => {
              if (user.availableDays < 0) return '#CCCCCC';
              if (user.availableDays === 0) return 'transparent';
              return theme.palette.primary.main;
            })(),
          }}
          variant="determinate"
          value={
            user.availableDays < 0
              ? ((TOTAL_VACATION_DAYS - user.availableDays) * 100) / TOTAL_VACATION_DAYS
              : 100 - ((TOTAL_VACATION_DAYS + user.availableDays) * 100) / TOTAL_VACATION_DAYS
          }
          aria-hidden="true"
        />
        <DarkTooltip
          title={(() => {
            if (user.availableDays === 0) {
              return 'No days left for the current period';
            }
            if (user.availableDays === -TOTAL_VACATION_DAYS) {
              return 'No days left for the next period';
            }
            if (user.availableDays < 0) {
              return 'Days left for the next period';
            }
            return 'Total number of available days';
          })()}
        >
          <Box
            sx={{
              top: 7,
              left: 7,
              bottom: 7,
              right: 7,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: '#F7F7F9',
              cursor: 'pointer',
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                color:
                  currentRemainingDays === 0 || user.availableDays === -TOTAL_VACATION_DAYS
                    ? '#CCCCCC'
                    : undefined,
              }}
              variant="subtitle1"
              id="currentVacationDays"
              aria-label={accessibleRemainingText}
            >
              {currentRemainingDays}
            </Typography>
          </Box>
        </DarkTooltip>
      </Box>
    </>
  );
};

VacationTileCurrent.propTypes = {
  user: PropTypes.object,
};

VacationTileCurrent.defaultProps = {
  user: {
    availableDays: 0,
  },
};

export default VacationTileCurrent;
